import React, { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import cx from 'classnames';
import ScrollbarWrapper from './ScrollbarWrapper/index.js';
import { BodyWrapper } from './BodyWrapper/index.js';
import styles from './index.module.scss.js';
import { Tabs } from '../../tabs/Tabs/index.js';
import { SelectV2 } from '../../../form/SelectV2/index.js';
import { Loader } from '../../loaders/Loader/index.js';

const options = [
  {
    id: "1",
    name: "10"
  },
  {
    id: "2",
    name: "20"
  },
  {
    id: "3",
    name: "30"
  },
  {
    id: "4",
    name: "40"
  },
  {
    id: "5",
    name: "50"
  }
];
const Table = ({
  emptyMessage,
  customScrollbar = true,
  column = [],
  data = [],
  rowCountSelect,
  isLoading,
  handleClick,
  onCountChange = () => {
  },
  onTabChange = () => {
  },
  onMouseEnter = () => {
  },
  onMouseLeave = () => {
  },
  tabs,
  className,
  style,
  v2 = true,
  liveLoad,
  opacity = "0.4",
  tableHeight = 580,
  autoHeight,
  autoHeightMin,
  autoHeightMax,
  customControlledRowCount,
  animation = {
    "data-aos": "fade-up",
    "data-aos-delay": "300",
    "data-aos-duration": "1000"
  },
  withShadow = false,
  noGap = false,
  countRowNum = 80,
  ...props
}) => {
  const scrollableContentRef = useRef(null);
  const [selected, setSelected] = useState([options[0].id]);
  const [countRow, setCountRow] = useState(10);
  const onSelect = useCallback(
    (v) => {
      setSelected(v);
      const count = Number(options.find((item) => item.id === v[0])?.name) || 10;
      onCountChange(count);
      setCountRow(count);
    },
    [options]
  );
  const tableWidth = useMemo(
    () => ({
      lg: column.reduce((acc, curr) => acc + (curr.width?.lg || 180), 0),
      md: column.reduce((acc, curr) => acc + (curr.width?.md || 180), 0),
      sm: column.reduce((acc, curr) => acc + (curr.width?.sm || 180), 0)
    }),
    [column]
  );
  const height = useMemo(() => {
    if (!data.length) return 200;
    if (rowCountSelect || customControlledRowCount) {
      if (countRow > data.length) {
        return data.length * 48 + countRowNum;
      }
      return countRow * 48 + countRowNum;
    }
    return parseInt(String(tableHeight), 10) || 580;
  }, [data, rowCountSelect, countRow, countRowNum, customControlledRowCount]);
  useEffect(() => {
    if (customControlledRowCount) {
      setCountRow(customControlledRowCount.count);
    }
  }, [customControlledRowCount]);
  return /* @__PURE__ */ React.createElement(
    "div",
    {
      ...props,
      ...animation,
      style: {
        ...style,
        "--table-text-opacity": opacity,
        "--table-title-color": "rgb(152, 167, 181)"
      },
      className: cx(styles.box, "list-table", className, {
        [styles.v2]: v2
      }),
      onMouseEnter,
      onMouseLeave
    },
    /* @__PURE__ */ React.createElement(
      "div",
      {
        className: cx(
          styles.box__inner,
          { [styles["no-gap"]]: noGap },
          {
            "table-scroll-wrapper": customScrollbar
          }
        )
      },
      (tabs?.buttons.length || rowCountSelect) && /* @__PURE__ */ React.createElement("div", { className: styles.top }, !!tabs?.buttons.length && /* @__PURE__ */ React.createElement(
        Tabs,
        {
          defaultActiveIndex: tabs.defaultIndex,
          buttons: tabs.buttons,
          animation: null,
          onChange: onTabChange
        }
      ), rowCountSelect && /* @__PURE__ */ React.createElement(
        SelectV2,
        {
          style: {
            width: "80px",
            minWidth: "80px"
          },
          layoutStyle: {
            width: "100%"
          },
          popupWidth: "125px",
          thumbLabel: "",
          formType: true,
          options,
          selectedIds: selected,
          onSelect,
          className: cx(styles.select, "table-count-select"),
          isCenter: true
        }
      )),
      /* @__PURE__ */ React.createElement(
        ScrollbarWrapper,
        {
          scrollbarsOff: !customScrollbar,
          autoHeight,
          height,
          autoHeightMin,
          autoHeightMax,
          withShadow,
          noHScrollTop: customScrollbar && !customControlledRowCount && !rowCountSelect
        },
        /* @__PURE__ */ React.createElement(
          "div",
          {
            ref: scrollableContentRef,
            className: cx(styles.table, "list-table__table", {
              [styles.v2]: v2,
              [styles.live]: liveLoad
            }),
            style: {
              "--min-width-lg": `${tableWidth.lg}px`,
              "--min-width-md": `${tableWidth.md}px`,
              "--min-width-sm": `${tableWidth.sm}px`
            }
          },
          /* @__PURE__ */ React.createElement("div", { className: styles.head }, /* @__PURE__ */ React.createElement("div", { className: styles.row }, column.map(({ header, key, width }, i) => /* @__PURE__ */ React.createElement(
            "div",
            {
              className: styles.th,
              style: {
                "--lg": `${width?.lg}px` || "250px",
                "--md": `${width?.md}px` || "250px",
                "--sm": `${width?.sm}px` || "250px"
              },
              key: `${key}__${i}`
            },
            header?.()
          )))),
          /* @__PURE__ */ React.createElement("div", { className: styles.body }, /* @__PURE__ */ React.createElement(
            BodyWrapper,
            {
              liveLoad,
              isEmpty: !data.length,
              countRow: countRow > data.length ? data.length : countRow
            },
            (!isLoading ? data : []).map((item, index) => /* @__PURE__ */ React.createElement(
              "div",
              {
                className: cx(styles.row, {
                  [styles.v2]: v2,
                  [styles.dark]: liveLoad ? item?.dark : !((index || 0) % 2)
                }),
                key: `${liveLoad ? item.id : `${item.id}_${index}`}`
              },
              column.map(({ key, col, width }, i) => /* @__PURE__ */ React.createElement(
                "div",
                {
                  className: cx(styles.td, {
                    [styles["td-first"]]: i === 0
                  }),
                  key: `${index}-${key}`,
                  style: {
                    "--lg": `${width?.lg}px` || "250px",
                    "--md": `${width?.md}px` || "250px",
                    "--sm": `${width?.sm}px` || "250px"
                  },
                  onClick: () => {
                    if (handleClick && i === 0) {
                      handleClick(item);
                    }
                  }
                },
                col?.(item[key], item) || /* @__PURE__ */ React.createElement("span", null, item[key])
              ))
            ))
          ), !data.length && !isLoading && /* @__PURE__ */ React.createElement("div", { className: styles["empty-msg"] }, emptyMessage || "There is no data"), isLoading && /* @__PURE__ */ React.createElement(Loader, { className: styles.loader }))
        )
      )
    )
  );
};
Table.displayName = "Table";

export { Table };
//# sourceMappingURL=index.js.map
